import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

// Components
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Calendar } from "../../components/ui/calendar";

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useCalendarEvents } from '../../hooks/event/useCalendarEvents';

// Services
import { getTenantIdFromStorage } from '../../services/authentication/supertokens';

// Icons
import { Calendar as CalendarIcon } from "lucide-react";

function OrganizationDashboard() {
    const navigate = useNavigate();
    const { getTenantPath } = useTenantNavigation();
    const tenantId = getTenantIdFromStorage();
    const {
        date,
        events,
        loading,
        error,
        onDateChange,
        getEventsOnDate
    } = useCalendarEvents(tenantId);

    const onClickDay = (clickedDate: Date): void => {
        const eventsOnDate = getEventsOnDate(clickedDate);
        if (eventsOnDate.length > 0) {
            navigate(getTenantPath(`/events/${clickedDate.toISOString().split('T')[0]}`));
        }
    };

    if (loading) {
        return (
            <OrganizationLayout showCreateEvent={true}>
                <div className="flex justify-center items-center h-64">
                    <Skeleton className="h-12 w-12 rounded-full" />
                </div>
            </OrganizationLayout>
        );
    }

    if (error) {
        return (
            <OrganizationLayout showCreateEvent={true}>
                <Alert variant="destructive">
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </OrganizationLayout>
        );
    }

    return (
        <OrganizationLayout showCreateEvent={true}>
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 pb-8">
                {/* Calendar Section */}
                <Card className="w-full lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Event Calendar</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="calendar-container w-full h-full">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={onDateChange}
                                className="h-full w-full flex"
                                classNames={{
                                    months: "flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 flex-1",
                                    month: "space-y-4 w-full flex flex-col",
                                    table: "w-full h-full border-collapse space-y-1",
                                    head_row: "flex w-full",
                                    head_cell: "text-base xs:text-lg sm:text-xl font-semibold text-center w-[14.28%]",
                                    row: "w-full mt-2 flex",
                                    cell: "text-base xs:text-lg sm:text-xl p-0.5 xs:p-1 sm:p-2 text-center w-[14.28%] h-12 xs:h-14 sm:h-16",
                                    button: "text-base xs:text-lg sm:text-xl w-full h-full flex items-center justify-center transition-transform duration-200 hover:scale-110 bg-gray-100 rounded-lg",
                                    caption: "flex justify-center pt-1 xs:pt-2 sm:pt-3 relative items-center mb-2 xs:mb-3 sm:mb-4",
                                    caption_label: "text-lg xs:text-xl sm:text-2xl font-medium",
                                    nav: "space-x-1 flex items-center",
                                    nav_button: "!bg-black text-white h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 max-w-[2.5rem] max-h-[2.5rem] p-0 text-base sm:text-lg md:text-xl flex items-center justify-center hover:scale-110 transition-transform rounded-lg"
                                }}
                                modifiers={{
                                    hasEvent: (date) => {
                                        const eventsOnDate = getEventsOnDate(date);
                                        return eventsOnDate.length > 0;
                                    },
                                    today: (date) => {
                                        const today = new Date();
                                        return date.getDate() === today.getDate() &&
                                            date.getMonth() === today.getMonth() &&
                                            date.getFullYear() === today.getFullYear();
                                    }
                                }}
                                modifiersStyles={{
                                    hasEvent: {
                                        fontWeight: 'bold',
                                        color: '#ffffff',
                                        backgroundColor: '#23d6e4',
                                        borderRadius: '0.5rem',
                                        transition: 'transform 0.2s'
                                    },
                                    today: {
                                        border: '3px solid #000000',
                                        borderRadius: '0.5rem'
                                    }
                                }}
                                onDayClick={onClickDay}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Upcoming Events Section */}
                <Card className="w-full shadow">
                    <CardHeader>
                        <CardTitle>Upcoming Events</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {(() => {
                                const upcomingEvents = events
                                    .filter(event => {
                                        const [year, month, day] = event.date.split('T')[0].split('-');
                                        const eventDate = new Date(Number(year), Number(month) - 1, Number(day));
                                        const now = new Date();
                                        
                                        if (eventDate.toDateString() === now.toDateString()) {
                                            const currentHour = now.getHours();
                                            const currentMinute = now.getMinutes();
                                            const [endHour, endMinute] = event.time_end ? event.time_end.split(':').map(Number) : [23, 59];
                                            
                                            return currentHour < endHour || (currentHour === endHour && currentMinute < endMinute);
                                        }
                                        
                                        return eventDate > now;
                                    })
                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                    .slice(0, 3);

                                if (upcomingEvents.length === 0) {
                                    return (
                                        <div className="flex flex-col items-center justify-center py-12 text-center px-8">
                                            <CalendarIcon className="h-16 w-16 text-muted-foreground mb-4" />
                                            <p className="text-muted-foreground mb-2 font-semibold text-lg">No upcoming events</p>
                                            <p className="text-sm text-black/70 max-w-xs">Click the Create Event button to schedule an event</p>
                                        </div>
                                    );
                                }

                                return upcomingEvents.map(event => {
                                    const [year, month, day] = event.date.split('T')[0].split('-');
                                    const eventDate = new Date(Number(year), Number(month) - 1, Number(day));
                                    
                                    return (
                                        <Card 
                                            key={event.uuid}
                                            className="group relative overflow-hidden transition-all duration-200 hover:scale-[1.02] cursor-pointer bg-black text-white border-white/10"
                                            onClick={() => navigate(getTenantPath(`/event/${event.uuid}`))}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center justify-between">
                                                        <h3 className="font-semibold text-lg truncate text-white">
                                                            {event.title}
                                                        </h3>
                                                        <Badge variant="secondary" className="bg-white/10 text-white hover:bg-white/20 shrink-0 ml-2">
                                                            {format(eventDate, 'MMM d')}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-white/60 truncate">
                                                        {format(eventDate, 'EEEE, MMMM d, yyyy')}
                                                    </p>
                                                    {event.location && (
                                                        <p className="text-sm text-white/60 truncate flex items-center gap-1">
                                                            <span className="text-primary shrink-0">📍</span> {event.location}
                                                        </p>
                                                    )}
                                                    {event.time_start && event.time_end && (
                                                        <p className="text-sm text-white/60 truncate">
                                                            {format(new Date(`2000-01-01T${event.time_start}`), 'h:mm a')} - {format(new Date(`2000-01-01T${event.time_end}`), 'h:mm a')}
                                                        </p>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                });
                            })()}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </OrganizationLayout>
    );
}

export default OrganizationDashboard;